import React from "react";
import { io, Socket } from "socket.io-client";

// Import from constants
import { API_URL_SOCKET } from "@/constants/Statics";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";
import { toast } from "sonner";

type ServerToClientEvents = Record<string, (...args: any[]) => void>;
type ClientToServerEvents = Record<string, (...args: any[]) => void>;

// Auth error codes that should trigger logout
const AUTH_ERROR_MESSAGES = [
  'invalid token',
  'token expired',
  'unauthorized',
  'unauthenticated',
  'authentication failed',
  'not authenticated',
];

// Function to check if error is auth-related
function isSocketAuthError(error: any): boolean {
  const errorMessage = (error?.message || error?.toString() || '').toLowerCase();
  return AUTH_ERROR_MESSAGES.some(msg => errorMessage.includes(msg));
}

// Function to handle auth error logout
function handleSocketAuthError(message?: string) {
  const authState = store.getState().auth;
  if (!authState.isAuthenticated) return;

  console.warn('[Socket Auth Error]: Token invalid, logging out...');

  toast.error(message || 'Session expired. Please login again.', {
    duration: 3000,
  });

  store.dispatch(logout());
  localStorage.clear();
  sessionStorage.clear();

  setTimeout(() => {
    window.location.href = '/login';
  }, 500);
}

export const socket: Socket<ServerToClientEvents, ClientToServerEvents> =
  io(API_URL_SOCKET);

// Add global error handler for socket
socket.on('connect_error', (error) => {
  console.error('[Socket connect_error]:', error);
  if (isSocketAuthError(error)) {
    handleSocketAuthError(error.message);
  }
});

socket.on('error', (error: any) => {
  console.error('[Socket error]:', error);
  if (isSocketAuthError(error)) {
    handleSocketAuthError(error?.message);
  }
});

// Handle authentication-specific events if server sends them
socket.on('auth_error', (data: any) => {
  console.error('[Socket auth_error]:', data);
  handleSocketAuthError(data?.message || 'Authentication failed');
});

socket.on('unauthorized', (data: any) => {
  console.error('[Socket unauthorized]:', data);
  handleSocketAuthError(data?.message || 'Unauthorized access');
});

export const SocketContext = React.createContext<Socket<
  ServerToClientEvents,
  ClientToServerEvents
> | null>(null);